import { IClient } from 'ngx-mat-esign360/lib/transaction/model/client.model';
import { IDocumentSignField } from 'ngx-mat-esign360/lib/transaction/model/document-field.model';
import { TxDocument } from 'ngx-mat-esign360/lib/transaction/model/document.model';
import { SigningState } from 'ngx-mat-esign360/lib/transaction/model/signing-state.model';
import {
  checkCanReadRelation,
  checkMustReadRelation,
  checkMustSignRelation,
} from '../utils/client-doc-relation.util';
import { ClientDocumentRelation } from './client-document-relation.model';
import { IClientStateData } from './client-state-data.model';
import { PrerequisiteState } from './prerequisite-state.model';
import { IPrerequisite } from './prerequisite.model';
import { ITransactionSection, TransactionSection } from './transaction-section.model';

export interface ITransaction {
  id: string;
  externalId: string;
  correlationId: string;
  title: string;
  signingState: SigningState;
  areDocumentsLoaded: boolean;
  startTime: Date;
  redirectUrl: string;
  clients: IClient[];
  sections: ITransactionSection[];
}

export class Transaction implements ITransaction {
  constructor(
    public id: string,
    public externalId: string,
    public correlationId: string,
    public title: string,
    public signingState: SigningState,
    public areDocumentsLoaded: boolean,
    public startTime: Date,
    public redirectUrl: string,
    public clients: IClient[] = [],
    public sections: TransactionSection[] = []
  ) {}

  get allDocuments(): TxDocument[] {
    return this.sections.reduce((documents, section) => {
      return documents.concat(section.documents);
    }, []);
  }

  getClientById(id: string): IClient {
    return this.clients.find((client) => client.id === id);
  }

  getClientStateDataById(id: string): IClientStateData {
    return this.clients.find((client) => client.id === id)?.stateData;
  }

  getDocumentById(id: string): TxDocument {
    let resultDocument = null;

    this.sections.some((section) => {
      const foundDocument = section.documents.find((document) => document.id === id);
      if (foundDocument) {
        resultDocument = foundDocument;
      }
      return !!foundDocument;
    });
    return resultDocument;
  }

  getDocumentsByClientId(clientId: string): TxDocument[] {
    return this.sections.reduce((documents, section) => {
      return documents.concat(section.getDocumentsByClientId(clientId));
    }, []);
  }

  getMandatoryDocumentsByClientId(clientId: string): TxDocument[] {
    return this.getDocumentsByClientId(clientId).filter((document) => {
      return checkMustSignRelation(document, clientId) || checkMustReadRelation(document, clientId);
    });
  }

  getOptionalDocumentsByClientId(clientId: string): TxDocument[] {
    return this.getDocumentsByClientId(clientId).filter((document) => {
      return checkCanReadRelation(document, clientId);
    });
  }

  getDocumentEditedByClient(clientId: string): TxDocument {
    return this.getDocumentById(this.getClientStateDataById(clientId)?.documentId);
  }

  getSignFieldEditedByClient(clientId: string): IDocumentSignField {
    const clientStateData = this.getClientStateDataById(clientId);

    if (clientStateData) {
      const document = this.getDocumentById(clientStateData?.documentId);
      if (document) {
        return document.getSignFieldById(clientStateData.signatureId);
      }
    }
    return null;
  }

  getFirstUnfinishedMandatoryDocForClient(clientId: string): TxDocument | undefined {
    return this.getMandatoryDocumentsByClientId(clientId).find((document) => {
      const signer = document.getSignerById(clientId);
      return (
        signer?.signingState === SigningState.NotStarted ||
        signer?.signingState === SigningState.InProcess
      );
    });
  }

  hasClientFinishedAllPrerequisites(clientId: string): boolean {
    return this.getClientById(clientId).prerequisites.every(
      (prerequisite) => prerequisite.state === PrerequisiteState.Fulfilled
    );
  }

  getFirstUnfinishedPrerequisiteForClient(clientId: string): IPrerequisite | undefined {
    return this.getClientById(clientId).prerequisites.find((prerequisite) => {
      return prerequisite.state !== PrerequisiteState.Fulfilled;
    });
  }

  hasClientFinishedAllPreviousDocs(documentId: string, clientId: string): boolean {
    const clientDocuments = this.getDocumentsByClientId(clientId);

    for (const document of clientDocuments) {
      if (document.id === documentId) {
        break;
      }
      if (
        !document.hasBeenProcessedBySigner(clientId) &&
        !checkCanReadRelation(document, clientId)
      ) {
        return false;
      }
    }
    return true;
  }

  hasClientFinishedAllPreviousSteps(documentId: string, clientId: string): boolean {
    return (
      this.hasClientFinishedAllPrerequisites(clientId) &&
      this.hasClientFinishedAllPreviousDocs(documentId, clientId)
    );
  }

  isCancelled(): boolean {
    return this.signingState === SigningState.Cancelled;
  }

  isRefused(): boolean {
    return this.signingState === SigningState.Refused;
  }

  isFinished(): boolean {
    return this.signingState === SigningState.Finished;
  }

  isErroredOut(): boolean {
    return this.signingState === SigningState.Error;
  }

  isTimedOut(): boolean {
    return this.signingState === SigningState.Timeout;
  }

  isBlocked(): boolean {
    return this.signingState === SigningState.Blocked;
  }

  isTerminated(): boolean {
    return (
      this.isCancelled() ||
      this.isRefused() ||
      this.isErroredOut() ||
      this.isTimedOut() ||
      this.isBlocked()
    );
  }

  isInFinalSigningState(): boolean {
    return this.isTerminated() || this.isFinished();
  }
}
